import { useEffect, useRef, useState } from "react";
import { RiEyeLine, RiEyeOffLine, RiRefreshLine } from "react-icons/ri";
import { memoryRequest } from "api/assistantMemory";
import MemoryButton from "./MemoryButton";
import MemoryAlert from "./MemoryAlert";
import { formatMemoryDate } from "./formatMemoryDate";
import css from "./memory.module.scss";

export default function MemoryEvidence({ fact }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [error, setError] = useState("");
  const pending = useRef(null);
  useEffect(() => () => pending.current?.abort(), []);

  const load = async () => {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setOpen(true);
    setLoading(true);
    setEvidence([]);
    setError("");
    try {
      const data = await memoryRequest(
        `/memories/${encodeURIComponent(fact.id)}/evidence?revision=${fact.revision}`,
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (data.revision !== fact.revision)
        throw new Error("Memory changed. Reopen its evidence.");
      setEvidence(data.evidence);
    } catch (e) {
      if (!controller.signal.aborted) setError(e.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };
  const close = () => {
    pending.current?.abort();
    setOpen(false);
    setEvidence([]);
    setError("");
  };

  const excerpt = (item, index) => (
    <blockquote key={index} className={css.evidenceQuote}>
      <p className={css.evidenceQuoteText}>{item.quote}</p>
      <span className={css.evidenceSource}>
        {item.speaker || "Original statement"}
        {item.timestampMs != null &&
          ` · ${formatMemoryDate(item.timestampMs)}, ${new Date(item.timestampMs).toLocaleTimeString("en-GB")}`}
      </span>
    </blockquote>
  );

  return (
    <>
      <MemoryButton
        size="xs"
        icon={open ? <RiEyeOffLine /> : <RiEyeLine />}
        aria-expanded={open}
        onClick={open ? close : load}
      >
        {open ? "Hide evidence" : "Show evidence"}
      </MemoryButton>
      {open && (
        <div
          role="group"
          className={css.evidencePanel}
          aria-label="Supporting evidence"
        >
          {loading && (
            <p className={css.evidenceStatus} role="status">
              <span className={css.spinner} aria-hidden="true" /> Loading
              evidence…
            </p>
          )}
          {error && (
            <MemoryAlert tone="danger" role="alert">
              <span>{error}</span>
              <span className={css.alertRow}>
                <MemoryButton
                  size="xs"
                  variant="ghost"
                  icon={<RiRefreshLine />}
                  onClick={load}
                >
                  Retry evidence
                </MemoryButton>
              </span>
            </MemoryAlert>
          )}
          {!loading && !error && !evidence.length && (
            <p className={css.evidenceStatus}>
              The original evidence is no longer available.
            </p>
          )}
          {evidence.map((item, index) => (
            <div key={index} className={css.evidenceItem}>
              <p className={css.evidenceTitle}>
                {item.meetingTitle ||
                  (item.kind === "meeting" ? "Meeting" : "Your conversation")}
              </p>
              <span className={css.evidenceWhen}>
                {formatMemoryDate(item.observedAt)}
              </span>
              {excerpt(item, "primary")}
              {!!item.context?.length && (
                <span className={css.evidenceContextLabel}>
                  Surrounding context
                </span>
              )}
              {item.context?.map(excerpt)}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
